import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { Link, useParams } from "wouter";
import { ArrowLeft, Calendar, Clock, Users, User, Euro, CheckCircle, AlertCircle, Loader2, ListOrdered, CalendarPlus, ChevronDown, CalendarDays, Lock, XCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState, useEffect } from "react";
import { useEmbedMode, appendEmbedParam } from "@/hooks/useEmbedMode";
import { toast } from "sonner";
import { hasAnyRole } from "@/lib/roles";
import { TenantLogo } from "@/components/TenantLogo";
import { SessionBookingDialog } from "@/components/SessionBookingDialog";
import { useFeatures } from "@/components/FeatureGuard";
import { trackCourseViewed, trackCourseBooked } from "@/lib/tracking";

const DAYS_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

const PAYMENT_INFO: Record<string, { label: string; description: string; variant: "default" | "secondary" | "outline" }> = {
  selbstzahler: { 
    label: "Selbstzahler", 
    description: "Dieser Kurs wird privat bezahlt.",
    variant: "outline" 
  },
  krankenkasse: { 
    label: "Krankenkasse", 
    description: "Die Kosten werden von Ihrer Krankenkasse übernommen.",
    variant: "default" 
  },
  erstattung: { 
    label: "Erstattung möglich", 
    description: "Eine Kostenrückerstattung durch Ihre Krankenkasse ist möglich.",
    variant: "secondary" 
  },
};

export default function CourseDetail() {
  const params = useParams<{ id: string; sessionId: string }>();
  const courseId = parseInt(params.id || "0");
  const preselectedSessionId = params.sessionId ? parseInt(params.sessionId) : undefined;
  const { user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isEmbedded = useEmbedMode();
  
  // Feature flags
  const { features } = useFeatures();
  
  const { data: course, isLoading, error } = trpc.course.getById.useQuery(
    { id: courseId },
    { enabled: courseId > 0 }
  );

  // Track course view when course data is loaded
  useEffect(() => {
    if (course && courseId > 0) {
      trackCourseViewed(courseId, course.name, course.instructor?.name);
    }
  }, [course, courseId]);

  // Get availability info including waitlist
  const { data: availability } = trpc.booking.getCourseAvailability.useQuery(
    { courseId },
    { enabled: courseId > 0 }
  );

  // Get calendar links
  const { data: calendarLinks } = trpc.course.getCalendarLinks.useQuery(
    { id: courseId },
    { enabled: courseId > 0 }
  );

  // Get available blocks for booking (only upcoming) — for the "book whole block" button
  const { data: availableBlocks, refetch: refetchBlocks } = trpc.courseBlock.getAvailable.useQuery(
    { courseId },
    { enabled: courseId > 0 && !!course?.isBlockCourse }
  );

  // Get current blocks for display (upcoming + active) — to show block info and individual sessions
  const { data: currentBlocks } = trpc.courseBlock.getCurrent.useQuery(
    { courseId },
    { enabled: courseId > 0 && !!course?.isBlockCourse }
  );

  // For normal courses only, load nextSession
  const { data: nextSession } = trpc.course.getNextSession.useQuery(
    { courseId },
    { enabled: courseId > 0 && !course?.isBlockCourse }
  );

  // State for individual session booking from block view
  const [selectedSession, setSelectedSession] = useState<{ id: number; date: string } | null>(null);

  // Get preselected session details if coming from calendar
  const { data: preselectedSession } = trpc.session.getById.useQuery(
    { id: preselectedSessionId! },
    { enabled: !!preselectedSessionId }
  );

  const now = new Date();

  // Returns true if a session is still bookable (start datetime is in the future)
  function isBookable(session: { sessionDate: string; isCancelled: number | boolean } | null | undefined): boolean {
    if (!session || session.isCancelled) return false;
    const startTime = course?.startTime || "00:00";
    const [sh, sm] = startTime.split(":").map(Number);
    const sessionStart = new Date(session.sessionDate + "T00:00:00");
    sessionStart.setHours(sh, sm, 0, 0);
    return sessionStart > now;
  }

  // Use preselectedSession only if bookable, otherwise fall back to nextSession
  const targetSession = (isBookable(preselectedSession) ? preselectedSession : null) ?? (isBookable(nextSession) ? nextSession : null) ?? null;

  // Get the user's existing course-level booking (pending/confirmed)
  const { data: myBooking, refetch: refetchMyBooking } = trpc.booking.getMyBookingForCourse.useQuery(
    { courseId },
    { enabled: courseId > 0 && !!user }
  );

  // Check if user already booked the target session
  const { data: mySessionBooking, refetch: refetchMySessionBooking } = trpc.sessionBooking.getMyBookingForSession.useQuery(
    { sessionId: targetSession?.id ?? 0 },
    { enabled: !!targetSession && !!user }
  );

  const handleDownloadIcal = () => {
    if (!calendarLinks?.icalContent || !course) return;
    const blob = new Blob([calendarLinks.icalContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${course.name.replace(/\s+/g, "-")}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Kalenderdatei heruntergeladen");
  };

  const handleDownloadBlockIcal = async (blockId: number) => {
    try {
      const response = await fetch(`/api/calendar/block/${blockId}`);
      if (!response.ok) throw new Error("Kalenderdatei konnte nicht geladen werden");
      
      const icalContent = await response.text();
      const blob = new Blob([icalContent], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${course?.name.replace(/\s+/g, "-")}-Block-${blockId}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Kalenderdatei heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen", {
        description: error instanceof Error ? error.message : "Unbekannter Fehler"
      });
    }
  };

  const [bookingOpen, setBookingOpen] = useState(false);
  const [sessionBookingOpen, setSessionBookingOpen] = useState(false);
  const [joinWaitlist, setJoinWaitlist] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    participantName: user?.name || "",
    participantEmail: user?.email || "",
    participantPhone: "",
    notes: "",
    smsConsent: false,
    courseBlockId: null as number | null,
  });

  const utils = trpc.useUtils();

  // Course-level registration (booking.create) — creates pending entry, no capacity check
  const bookingMutation = trpc.booking.create.useMutation({
    onSuccess: (result) => {
      if (course) {
        trackCourseBooked(courseId, course.name, result.id, false, formData.courseBlockId || undefined);
      }
      toast.success("Anmeldung eingereicht!", {
        description: "Ihre Anmeldung ist eingegangen. Sie erhalten eine Bestätigung per E-Mail sobald sie bearbeitet wurde.",
      });
      setBookingOpen(false);
      setFormData({ participantName: user?.name || "", participantEmail: user?.email || "", participantPhone: "", notes: "", smsConsent: false, courseBlockId: null });
      setSelectedBlockId(null);
      setJoinWaitlist(false);
      utils.course.getById.invalidate({ id: courseId });
      utils.booking.getCourseAvailability.invalidate({ courseId });
      utils.booking.getMyBookingForCourse.invalidate({ courseId });
      refetchMyBooking();
      refetchBlocks();
    },
    onError: (error) => {
      toast.error("Anmeldung fehlgeschlagen", { description: error.message });
    },
  });

  // Session booking mutation (for confirmed users to book a specific session)
  const sessionBookingMutation = trpc.sessionBooking.create.useMutation({
    onSuccess: (result) => {
      if (result.status === "waitlist") {
        toast.success("Auf Warteliste eingetragen!", {
          description: `Sie sind auf Position ${result.waitlistPosition} der Warteliste.`,
        });
      } else {
        toast.success("Termin gebucht!");
      }
      setSessionBookingOpen(false);
      utils.sessionBooking.getAvailability.invalidate();
      refetchMySessionBooking();
    },
    onError: (error) => {
      toast.error("Buchung fehlgeschlagen", { description: error.message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    bookingMutation.mutate({
      courseId,
      ...formData,
      joinWaitlist: undefined,
    });
  };

  const handleSessionBook = () => {
    if (!targetSession) return;
    sessionBookingMutation.mutate({
      sessionId: targetSession.id,
      participantName: user?.name || "",
      participantEmail: user?.email || "",
      joinWaitlist: false,
    });
  };

  // Handler to open booking dialog with pre-selected block
  const openBookingForBlock = (blockId: number) => {
    setSelectedBlockId(blockId);
    setFormData(prev => ({ ...prev, courseBlockId: blockId }));
    setBookingOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {!isEmbedded && <Header />}
        <main className="container py-8">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-12 w-96 mb-8" />
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        </main>
      </div>
    );
  }

  if (!course || error) {
    return (
      <div className="min-h-screen bg-background">
        {!isEmbedded && <Header />}
        <main className="container py-8">
          <Card className="mx-auto max-w-md">
            <CardContent className="flex flex-col items-center py-12 text-center">
              <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
              <h2 className="mb-2 text-xl font-semibold">Kurs nicht gefunden</h2>
              <p className="mb-6 text-muted-foreground">
                Der angeforderte Kurs existiert nicht oder wurde entfernt.
              </p>
              <Link href={appendEmbedParam("/")}>
                <Button>Zurück zur Übersicht</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const spotsLeft = availability?.availableSpots ?? (course.capacity - (course.currentBookings || 0));
  const isFull = availability?.isFull ?? spotsLeft <= 0;
  // Disable waitlist for block courses - they have their own block-based registration
  // Also check global waitlistEnabled feature flag
  const waitlistEnabled = features.waitlistEnabled && !course.isBlockCourse && (availability?.waitlistEnabled ?? course.waitlistEnabled ?? false);
  const waitlistCount = availability?.waitlistCount ?? 0;

  // For block courses: are there still bookable (upcoming) blocks?
  const hasAvailableBlocks = course.isBlockCourse && !!availableBlocks && availableBlocks.length > 0;

  return (
    <div className="min-h-screen bg-background">
      {!isEmbedded && <Header />}
      
      <main className="container py-8">
        {/* Back Link */}
        <Link href={appendEmbedParam("/")}>
          <Button variant="ghost" className="mb-6 gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Button>
        </Link>

        {/* Course Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-start gap-3 mb-4">
            {PAYMENT_INFO[course.paymentType] && (
              <Badge {...PAYMENT_INFO[course.paymentType]} className="text-sm">
                {PAYMENT_INFO[course.paymentType].label}
              </Badge>
            )}
            {course.category && (
              <Badge variant="outline">{course.category}</Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            {course.name}
          </h1>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Kursbeschreibung</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {course.description || "Keine Beschreibung verfügbar."}
                </p>
              </CardContent>
            </Card>

            {/* Schedule & Details */}
            <Card>
              <CardHeader>
                <CardTitle>Kursdetails</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Wochentag</p>
                      <p className="font-medium">{DAYS_DE[course.dayOfWeek]}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Uhrzeit</p>
                      <p className="font-medium">{course.startTime} - {course.endTime} Uhr</p>
                    </div>
                  </div>
                  {/* Only show capacity for non-block courses */}
                  {!course.isBlockCourse && (
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Teilnehmer</p>
                        <p className="font-medium">max. {course.capacity} Personen</p>
                      </div>
                    </div>
                  )}
                  {(course.priceInCents ?? 0) > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary/10">
                        <Euro className="h-5 w-5 text-secondary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Preis</p>
                        <p className="font-medium">{((course.priceInCents ?? 0) / 100).toFixed(2)} €</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Info */}
                {PAYMENT_INFO[course.paymentType] && (
                  <div className="mt-6 rounded-lg bg-muted/50 p-4">
                    <p className="text-sm text-muted-foreground">
                      <strong>Zahlungsart:</strong> {PAYMENT_INFO[course.paymentType].description}
                    </p>
                  </div>
                )}

                {/* Add to Calendar Button - Only show for non-block courses */}
                {calendarLinks && !course.isBlockCourse && (
                  <div className="mt-6">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full gap-2">
                          <CalendarPlus className="h-4 w-4" />
                          Zum Kalender hinzufügen
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={handleDownloadIcal}>
                          <Calendar className="mr-2 h-4 w-4" />
                          iCal / Apple Kalender
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={calendarLinks.googleCalendarUrl} target="_blank" rel="noopener noreferrer">
                            <Calendar className="mr-2 h-4 w-4" />
                            Google Kalender
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={calendarLinks.outlookCalendarUrl} target="_blank" rel="noopener noreferrer">
                            <Calendar className="mr-2 h-4 w-4" />
                            Outlook Kalender
                          </a>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instructor */}
            {course.instructor && (
              <Card>
                <CardHeader>
                  <CardTitle>Kursleitung</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <User className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{course.instructor.name}</h3>
                      {course.instructor.bio && (
                        <p className="mt-2 text-muted-foreground">{course.instructor.bio}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Booking */}
          <div>
            <Card className="sticky top-24 border-2 border-primary/20">
              <CardHeader>
                <CardTitle>Jetzt anmelden</CardTitle>
                <CardDescription>
                  {course.isBlockCourse
                    ? hasAvailableBlocks
                      ? "Dieser Kurs wird in Blöcken angeboten. Wählen Sie einen Block:"
                      : "Buchen Sie einzelne Termine aus dem laufenden Block:"
                    : (isFull 
                        ? "Dieser Kurs ist leider ausgebucht." 
                        : `Noch ${spotsLeft} ${spotsLeft === 1 ? "Platz" : "Plätze"} verfügbar`
                      )
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Block Course */}
                {course.isBlockCourse ? (
                  <div className="space-y-4">
                    {(currentBlocks ?? []).map((block) => {
                      const isUpcoming = block.status === "upcoming";
                      const isActive = block.status === "active";
                      const spotsLeftInBlock = block.maxParticipants - block.currentParticipants;
                      const blockIsFull = spotsLeftInBlock <= 0;

                      return (
                        <div key={block.id} className="rounded-lg border p-4 space-y-3">
                          {/* Block Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CalendarDays className="h-5 w-5 text-primary" />
                              <span className="font-semibold">
                                Block {new Date(block.startDate + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                              </span>
                              {isActive && <Badge variant="secondary" className="text-xs">Laufend</Badge>}
                            </div>
                            {isUpcoming && (
                              <Badge variant={blockIsFull ? "secondary" : "default"}>
                                {blockIsFull ? "Ausgebucht" : `${spotsLeftInBlock} ${spotsLeftInBlock === 1 ? "Platz" : "Plätze"} frei`}
                              </Badge>
                            )}
                          </div>

                          {/* Sessions */}
                          {block.sessions && block.sessions.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-muted-foreground">
                                {block.sessions.length} Termine:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {block.sessions.map((session) => {
                                  const bookable = isBookable(session);
                                  return (
                                    <button
                                      key={session.id}
                                      disabled={!bookable || !isActive}
                                      onClick={() => bookable && isActive && setSelectedSession({ id: session.id, date: session.sessionDate })}
                                      className={`inline-flex items-center px-2.5 py-1 rounded border text-xs font-medium transition-colors ${
                                        !bookable
                                          ? "bg-muted text-muted-foreground border-muted line-through cursor-default"
                                          : isActive
                                            ? "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 cursor-pointer"
                                            : "bg-muted/50 text-muted-foreground border-muted cursor-default"
                                      }`}
                                    >
                                      {new Date(session.sessionDate + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                                    </button>
                                  );
                                })}
                              </div>
                              {isActive && (
                                <p className="text-xs text-muted-foreground">Auf einen Termin tippen zum Buchen</p>
                              )}
                            </div>
                          )}

                          {/* Participants Info */}
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>{block.currentParticipants}/{block.maxParticipants} Teilnehmer</span>
                          </div>

                          {/* Action Buttons - only for upcoming bookable blocks */}
                          {isUpcoming && !blockIsFull && (
                            <div className="flex gap-2">
                              <Button
                                className="flex-1 bg-primary hover:bg-primary/90"
                                onClick={() => openBookingForBlock(block.id)}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                Für diesen Block anmelden
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleDownloadBlockIcal(block.id)}
                                title="Zum Kalender hinzufügen"
                              >
                                <CalendarPlus className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {(currentBlocks ?? []).length === 0 && (
                      <div className="text-center py-4">
                        <AlertCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Aktuell sind keine Blöcke verfügbar.<br />
                          Bitte kontaktieren Sie uns für weitere Informationen.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Regular Course: new 2-step flow */
                  <>
                    {myBooking ? (
                      /* User already has a registration */
                      <div className="space-y-4">
                        {/* Registration status */}
                        <div className={`flex items-center gap-3 rounded-lg border p-3 ${
                          myBooking.status === "confirmed" ? "bg-green-50 border-green-200" :
                          myBooking.status === "cancelled" ? "bg-red-50 border-red-200" :
                          "bg-orange-50 border-orange-200"
                        }`}>
                          {myBooking.status === "confirmed" ? (
                            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                          ) : myBooking.status === "cancelled" ? (
                            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
                          ) : (
                            <Loader2 className="h-5 w-5 text-orange-600 shrink-0 animate-spin" />
                          )}
                          <div>
                            <p className="text-sm font-medium">
                              {myBooking.status === "confirmed" ? "Anmeldung bestätigt" :
                               myBooking.status === "cancelled" ? "Anmeldung storniert" :
                               "Anmeldung ausstehend"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {myBooking.status === "confirmed"
                                ? "Sie können jetzt Termine buchen."
                                : myBooking.status === "cancelled"
                                ? "Ihre Anmeldung wurde storniert."
                                : "Ihre Anmeldung wird noch bearbeitet."}
                            </p>
                          </div>
                        </div>

                        {/* Next session booking */}
                        {targetSession && (
                          <div className="rounded-lg border p-3 space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {preselectedSessionId ? "Gewählter Termin" : "Nächster Termin"}
                            </p>
                            <p className="font-medium">
                              {new Date(targetSession.sessionDate + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                            <p className="text-sm text-muted-foreground">{course.startTime} – {course.endTime} Uhr</p>
                            {mySessionBooking?.status === "cancelled" ? (
                              <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-center text-muted-foreground">
                                <XCircle className="inline mr-1.5 h-4 w-4 text-gray-400" />
                                Termin storniert
                              </div>
                            ) : mySessionBooking ? (
                              <Button className="w-full" disabled variant="outline">
                                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                                Termin bereits gebucht
                              </Button>
                            ) : myBooking.status === "confirmed" ? (
                              <Button
                                className="w-full bg-primary hover:bg-primary/90"
                                onClick={handleSessionBook}
                                disabled={sessionBookingMutation.isPending}
                              >
                                {sessionBookingMutation.isPending ? (
                                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird gebucht...</>
                                ) : (
                                  <><CheckCircle className="mr-2 h-4 w-4" />Termin buchen</>
                                )}
                              </Button>
                            ) : (
                              <Button className="w-full" disabled>
                                <Lock className="mr-2 h-4 w-4" />
                                Termin buchen (nach Bestätigung)
                              </Button>
                            )}
                          </div>
                        )}

                        {!targetSession && myBooking.status === "confirmed" && (
                          <p className="text-sm text-center text-muted-foreground">Keine weiteren Termine geplant.</p>
                        )}
                      </div>
                    ) : (
                      /* No registration yet */
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        size="lg"
                        onClick={() => setBookingOpen(true)}
                      >
                        Zur Anmeldung
                      </Button>
                    )}
                  </>
                )}

                <p className="mt-4 text-xs text-center text-muted-foreground">
                  Bei Fragen erreichen Sie uns unter<br />
                  <a href={`tel:${config?.contact.phone.replace(/\s/g, '')}`} className="text-primary hover:underline">
                    {config?.contact.phone}
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Booking Dialog - Now used for both regular courses and block courses */}
        <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {isFull && !course.isBlockCourse ? "Warteliste beitreten" : "Kursanmeldung"}
              </DialogTitle>
              <DialogDescription>
                {!!course.isBlockCourse && selectedBlockId
                  ? `Anmeldung für "${course.name}" - Block ${availableBlocks?.find(b => b.id === selectedBlockId)?.startDate ? new Date(availableBlocks.find(b => b.id === selectedBlockId)!.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : ''}`
                  : (isFull 
                      ? `Tragen Sie sich in die Warteliste für "${course.name}" ein. Wir benachrichtigen Sie, sobald ein Platz frei wird.`
                      : `Melden Sie sich für "${course.name}" an.`
                    )
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Show selected block info for block courses */}
              {!!course.isBlockCourse && selectedBlockId && (
                <div className="rounded-lg bg-muted/50 p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    Block {availableBlocks?.find(b => b.id === selectedBlockId)?.startDate ? new Date(availableBlocks.find(b => b.id === selectedBlockId)!.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : ''}
                  </div>
                  {(() => {
                    const block = availableBlocks?.find(b => b.id === selectedBlockId);
                    if (block?.sessionDates && block.sessionDates.length > 0) {
                      return (
                        <p className="mt-1 text-muted-foreground">
                          {block.sessionDates.length} Termine ab {new Date(block.sessionDates[0]).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  required
                  value={formData.participantName}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantName: e.target.value }))}
                  placeholder="Ihr vollständiger Name"
                  readOnly={!!user}
                  className={user ? "bg-muted" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.participantEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantEmail: e.target.value }))}
                  placeholder="ihre@email.de"
                  readOnly={!!user}
                  className={user ? "bg-muted" : ""}
                />
              </div>

              {/* Info: account is always created automatically */}
              {!user && (
                <div className="rounded-lg border p-4 bg-blue-50 text-sm text-blue-800">
                  Mit der Anmeldung wird automatisch ein Konto für Sie erstellt. Sie erhalten eine Einladungs-E-Mail zum Einrichten Ihres Passworts.
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.participantPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantPhone: e.target.value }))}
                  placeholder="+49 ..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Anmerkungen</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Haben Sie besondere Wünsche oder Hinweise?"
                  rows={3}
                />
              </div>

              {/* SMS consent checkbox (GDPR compliant) - only show if smsEnabled */}
              {features.smsEnabled && formData.participantPhone && (
                <div className="flex items-start space-x-3 rounded-lg border p-4 bg-muted/50">
                  <Checkbox
                    id="smsConsent"
                    checked={formData.smsConsent}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, smsConsent: checked === true }))}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="smsConsent" className="font-medium cursor-pointer">
                      SMS-Benachrichtigungen erhalten
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Ich möchte per SMS über Terminänderungen (Absagen, Reaktivierungen) informiert werden. Diese Einwilligung kann jederzeit widerrufen werden.
                    </p>
                  </div>
                </div>
              )}

              {/* Waitlist confirmation checkbox when course is full (non-block courses only) */}
              {isFull && !course.isBlockCourse && (
                <div className="flex items-start space-x-3 rounded-lg border p-4 bg-muted/50">
                  <Checkbox
                    id="waitlist"
                    checked={joinWaitlist}
                    onCheckedChange={(checked) => setJoinWaitlist(checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="waitlist" className="font-medium cursor-pointer">
                      Auf Warteliste setzen
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Ich möchte benachrichtigt werden, sobald ein Platz frei wird.
                    </p>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className={`w-full ${isFull && !course.isBlockCourse ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"}`}
                disabled={bookingMutation.isPending || (isFull && !course.isBlockCourse && !joinWaitlist)}
              >
                {bookingMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird gesendet...
                  </>
                ) : isFull && !course.isBlockCourse ? (
                  <>
                    <ListOrdered className="mr-2 h-4 w-4" />
                    Auf Warteliste setzen
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Anmeldung absenden
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Session booking dialog for individual sessions in active blocks */}
        {selectedSession && (
          <SessionBookingDialog
            open={!!selectedSession}
            onOpenChange={(open) => { if (!open) setSelectedSession(null); }}
            sessionId={selectedSession.id}
            sessionDate={selectedSession.date}
            courseName={course.name}
            courseTime={`${course.startTime} – ${course.endTime} Uhr`}
            onSuccess={() => setSelectedSession(null)}
          />
        )}
      </main>
    </div>
  );
}

function Header() {
  const { user, loading } = useAuth();
  
  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <TenantLogo className="h-12 w-auto" />
        </Link>
        <nav className="flex items-center gap-4">
        </nav>
      </div>
    </header>
  );
}
